import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inviteCreateSchema, type InviteSummary, type UserSummary } from '@agency/contracts';
import { Badge, Button, Drawer, EmptyState, Notice, SelectField, Skeleton, TextField, useToast, scrollRegion } from '@agency/ui';
import { ApiClientError, del, get, patch, post } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { fmtDate } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';

const errText = (e: unknown) => (e instanceof ApiClientError ? `${e.message}${e.requestId ? ` (Ref ${e.requestId})` : ''}` : 'Something went wrong. Try again.');
type RoleOption = { key: string; name: string };

export function TeamPage() {
  const { can, user: me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);
  const q = useQuery<UserSummary[], ApiClientError>({ queryKey: ['team'], queryFn: () => get('/users') });
  const roles = useQuery<RoleOption[], ApiClientError>({ queryKey: ['role-options'], queryFn: () => get('/users/role-options') });
  const invites = useQuery<InviteSummary[], ApiClientError>({ queryKey: ['invites'], queryFn: () => get('/users/invites'), enabled: can('users.invite') });

  async function update(u: UserSummary, body: { roleKey?: string; active?: boolean }) {
    try {
      await patch(`/users/${u.id}`, body);
      toast({ tone: 'success', message: `Updated ${u.name}. Their sessions were refreshed so new access applies immediately.` });
    } catch (e) {
      toast({ tone: 'error', message: errText(e) });
    }
    await qc.invalidateQueries({ queryKey: ['team'] });
  }
  const endSessions = useMutation({
    mutationFn: (u: UserSummary) => post<{ revoked: number }>(`/users/${u.id}/revoke-sessions`),
    onSuccess: (r, u) => toast({ tone: 'success', message: r.revoked ? `Signed ${u.name} out of ${r.revoked} device${r.revoked === 1 ? '' : 's'}.` : `${u.name} had no active sessions.` }),
    onError: (e) => toast({ tone: 'error', message: errText(e) }),
  });
  const revokeInvite = useMutation({
    mutationFn: (i: InviteSummary) => del(`/users/invites/${i.id}`),
    onSuccess: async () => { toast({ tone: 'success', message: 'Invitation revoked.' }); await qc.invalidateQueries({ queryKey: ['invites'] }); },
    onError: (e) => toast({ tone: 'error', message: errText(e) }),
  });

  const pending = (invites.data ?? []).filter((i) => i.status === 'pending');
  return (
    <>
      <div className="pagehead">
        <div><h1>Team</h1><p>Roles decide what each person can see and do. The last active Owner can never be removed or demoted.</p></div>
        {can('users.invite') ? <div className="pagehead__actions"><Button variant="primary" icon="plus" onClick={() => setInviting(true)}>Invite someone</Button></div> : null}
      </div>
      {q.isLoading ? <Skeleton width="100%" height={160} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? <EmptyState title="No team members" icon="users" /> : (
        <div className="ag-table-wrap" {...scrollRegion('Team members')}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Team members</caption>
            <thead><tr><th scope="col">Person</th><th scope="col">Role</th><th scope="col">Two-step</th><th scope="col">State</th>{can('users.manage') ? <th scope="col"><span className="ag-visually-hidden">Actions</span></th> : null}</tr></thead>
            <tbody>
              {q.data!.map((u) => (
                <tr key={u.id}>
                  <td><span className="ag-cell-2"><span>{u.name}{u.id === me?.id ? ' (you)' : ''}</span><span>{u.email}</span></span></td>
                  <td style={{ minWidth: 200 }}>
                    {can('users.manage') ? (
                      <SelectField label={`Role for ${u.name}`} value={u.roleKey} onChange={(e) => void update(u, { roleKey: e.target.value })}>
                        {(roles.data ?? [{ key: u.roleKey, name: u.roleName }]).map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                      </SelectField>
                    ) : u.roleName}
                  </td>
                  <td><Badge tone={u.mfaEnabled ? 'success' : 'neutral'}>{u.mfaEnabled ? 'On' : 'Off'}</Badge></td>
                  <td>
                    {can('users.manage') ? (
                      <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                        <input type="checkbox" checked={u.active} onChange={(e) => void update(u, { active: e.target.checked })} /> Active
                      </label>
                    ) : <Badge tone={u.active ? 'success' : 'neutral'}>{u.active ? 'Active' : 'Deactivated'}</Badge>}
                  </td>
                  {can('users.manage') ? (
                    <td style={{ textAlign: 'right' }}>
                      <Button size="sm" variant="ghost" loading={endSessions.isPending && endSessions.variables?.id === u.id} onClick={() => endSessions.mutate(u)} aria-label={`Sign out everywhere: ${u.name}`}>Sign out everywhere</Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {can('users.invite') ? (
        <section className="panel" aria-labelledby="invites-h" style={{ marginTop: 24 }}>
          <h2 id="invites-h">Invitations</h2>
          {invites.isLoading ? <Skeleton width="100%" height={60} /> : invites.isError ? <QueryError error={invites.error} onRetry={() => void invites.refetch()} /> : (invites.data ?? []).length === 0 ? (
            <p className="ag-muted">No invitations yet. Invite someone to give them access with a role you choose.</p>
          ) : (
            <div className="ag-table-wrap" {...scrollRegion('Invitations')}>
              <table className="ag-table">
                <caption className="ag-visually-hidden">Invitations</caption>
                <thead><tr><th scope="col">Email</th><th scope="col">Role</th><th scope="col">Status</th><th scope="col">Expires</th><th scope="col"><span className="ag-visually-hidden">Actions</span></th></tr></thead>
                <tbody>
                  {invites.data!.map((i) => (
                    <tr key={i.id}>
                      <td>{i.email}</td>
                      <td>{i.roleName}</td>
                      <td><Badge tone={i.status === 'pending' ? 'info' : i.status === 'accepted' ? 'success' : 'neutral'}>{i.status}</Badge></td>
                      <td>{i.status === 'pending' ? fmtDate(i.expiresAt) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{i.status === 'pending' ? <Button size="sm" variant="ghost" onClick={() => revokeInvite.mutate(i)} aria-label={`Revoke invitation for ${i.email}`}>Revoke</Button> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pending.length ? <p className="ag-muted" style={{ marginTop: 8 }}>{pending.length} pending. Links work once and expire automatically.</p> : null}
        </section>
      ) : null}

      <InviteDrawer open={inviting} onClose={() => setInviting(false)} roles={roles.data ?? []} onSent={async () => { await qc.invalidateQueries({ queryKey: ['invites'] }); }} />
    </>
  );
}

function InviteDrawer({ open, onClose, roles, onSent }: { open: boolean; onClose: () => void; roles: RoleOption[]; onSent: () => Promise<void> }) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const role = roleKey || roles.find((r) => r.key === 'viewer')?.key || roles[0]?.key || '';
  const close = () => { setEmail(''); setRoleKey(''); setErrors({}); setError(null); onClose(); };
  return (
    <Drawer
      open={open}
      onClose={close}
      title="Invite someone"
      footer={<><Button onClick={close}>Cancel</Button><Button type="submit" form="invite-form" variant="primary" loading={busy}>Create invitation</Button></>}
    >
      <form id="invite-form" noValidate className="formgrid" onSubmit={async (e) => {
        e.preventDefault();
        const p = inviteCreateSchema.safeParse({ email, roleKey: role });
        if (!p.success) { setErrors(Object.fromEntries(p.error.issues.map((i) => [String(i.path[0]), i.path[0] === 'email' ? 'Enter a valid email address.' : 'Choose a role.']))); return; }
        setErrors({}); setError(null); setBusy(true);
        try {
          await post('/users/invites', p.data);
          toast({ tone: 'success', message: `Invitation created for ${p.data.email}. It expires automatically.` });
          await onSent();
          close();
        } catch (err) {
          if (err instanceof ApiClientError && err.fieldErrors.length) setErrors(Object.fromEntries(err.fieldErrors.map((f) => [f.field, f.message])));
          else setError(errText(err));
        } finally { setBusy(false); }
      }}>
        {error ? <Notice tone="error" title="Could not create the invitation">{error}</Notice> : null}
        <TextField label="Work email" type="email" autoComplete="off" required value={email} onChange={(e) => setEmail(e.target.value)} error={errors.email} />
        <SelectField label="Role" value={role} onChange={(e) => setRoleKey(e.target.value)} error={errors.roleKey} hint="You can only invite people into roles whose permissions you hold yourself.">
          {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </SelectField>
      </form>
    </Drawer>
  );
}
